import { useEffect } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  CalendarDays,
  CheckCircle2,
  ExternalLink,
  HeartHandshake,
  ShieldCheck,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";

const ARTICLES = {
  "primera-sesion-mediacion-familiar-como-prepararse": {
    category: "Mediación familiar · Guía práctica",
    title: "Primera sesión de mediación familiar: cómo prepararse y qué esperar",
    summary:
      "Una guía clara para llegar con información útil, entender el rol del mediador y participar en una conversación orientada a acuerdos.",
    publishedAt: "1 de septiembre de 2026",
    readingTime: "8 minutos",
    audience: "Para familias y mediadores",
    icon: HeartHandshake,
    accent: "text-teal-300",
    intro: [
      "Una mediación familiar no es un juicio abreviado ni una audiencia en la que el mediador decide quién tiene la razón. Es un proceso en el que una persona imparcial, sin poder decisorio, ayuda a las partes a buscar por sí mismas una solución al conflicto y sus efectos.",
      "Prepararse no significa llegar con una posición inamovible. Significa comprender qué se necesita resolver, reunir información básica y distinguir entre lo indispensable, lo negociable y aquello que todavía requiere orientación profesional.",
    ],
    sections: [
      {
        title: "¿En qué materias existe mediación previa?",
        paragraphs: [
          "En Chile, las causas sobre alimentos, cuidado personal y relación directa y regular deben someterse a mediación antes de presentar la demanda ante el Tribunal de Familia. Lo obligatorio es intentar el proceso previo; alcanzar un acuerdo depende siempre de la voluntad de quienes participan.",
          "Otras materias familiares pueden someterse a mediación cuando la ley lo permite y las partes lo acuerdan. También existen asuntos que no son mediables, por lo que conviene verificar la procedencia antes de iniciar el proceso.",
        ],
      },
      {
        title: "El rol del mediador: conducir el proceso, no imponer la solución",
        paragraphs: [
          "El mediador organiza la conversación, ayuda a identificar los temas relevantes y procura que ambas partes puedan participar en condiciones adecuadas. No representa a una de ellas, no reemplaza la asesoría jurídica individual y no dicta una decisión.",
          "La Ley N.º 19.968 exige que durante el proceso se resguarden principios como la igualdad, la voluntariedad, la confidencialidad, la imparcialidad y el interés superior de niños, niñas y adolescentes.",
        ],
      },
      {
        title: "Qué conviene reunir antes de la primera sesión",
        list: [
          "Una descripción breve del problema y de los temas que se necesitan resolver.",
          "Datos actualizados de contacto y antecedentes básicos de las personas involucradas.",
          "Información práctica: ingresos y gastos si se hablará de alimentos; horarios, domicilio, colegio y necesidades de cuidado si se abordará cuidado personal o relación directa y regular.",
          "Propuestas concretas que puedan cumplirse en la vida cotidiana.",
          "Preguntas que necesiten aclaración antes de aceptar un acuerdo.",
        ],
      },
      {
        title: "Cinco preguntas útiles para ordenar la conversación",
        list: [
          "¿Qué necesita quedar resuelto al terminar este proceso?",
          "¿Qué condiciones son esenciales para que el acuerdo sea realista?",
          "¿Qué aspectos podrían flexibilizarse?",
          "¿Cómo se verificará el cumplimiento y cómo se comunicarán los cambios?",
          "¿Qué ocurrirá si las circunstancias familiares cambian más adelante?",
        ],
      },
      {
        title: "Qué ocurre si existe acuerdo —y si no existe",
        paragraphs: [
          "Cuando hay acuerdo total o parcial, éste se registra en un acta. El mediador la remite al tribunal para su aprobación en todo aquello que no sea contrario a derecho. Una vez aprobada judicialmente, tiene valor de sentencia ejecutoriada.",
          "Si no se alcanza acuerdo, se emite un acta de mediación frustrada. Esa constancia permite, cuando corresponde, continuar por la vía judicial sin convertir las conversaciones de la mediación en un relato público del conflicto.",
        ],
      },
      {
        title: "¿Puede realizarse en línea?",
        paragraphs: [
          "Sí. La Ley N.º 19.968 permite que la mediación se realice por videoconferencia cuando existe acuerdo de las partes y el mediador dispone de los medios tecnológicos necesarios. Antes de aceptar esta modalidad conviene confirmar privacidad, identidad, conexión y la forma en que se firmarán o remitirán los documentos.",
        ],
      },
    ],
    takeawayTitle: "Una buena preparación reduce la incertidumbre",
    takeaway:
      "Llegar con antecedentes ordenados, propuestas realistas y preguntas claras permite aprovechar mejor la sesión. El objetivo no es ganar una discusión, sino construir un acuerdo comprensible, cumplible y compatible con la ley.",
    sources: [
      {
        label: "Ley N.º 19.968, Título V: mediación familiar",
        url: "https://www.bcn.cl/leychile/Navegar?idNorma=229557&idParte=8651422",
      },
      {
        label: "ChileAtiende: mediación familiar previa y obligatoria",
        url: "https://www.chileatiende.gob.cl/fichas/2344-mediacion-familiar-previa-y-obligatoria",
      },
    ],
    ctaEyebrow: "Mediación familiar en ColegaLink",
    ctaTitle: "Encuentra una alternativa colaborativa para avanzar.",
    ctaText:
      "Conoce cómo funciona la mediación familiar y revisa los perfiles disponibles en la plataforma.",
    ctaLabel: "Conocer mediación familiar",
    ctaPath: "/mediacion-familiar",
  },
  "inteligencia-artificial-practica-juridica-uso-responsable": {
    category: "Tecnología jurídica · Guía práctica",
    title: "Inteligencia artificial en la práctica jurídica: 7 controles antes de usarla",
    summary:
      "La IA puede acelerar tareas, pero no reemplaza la revisión profesional. Un método para usarla con criterio, trazabilidad y resguardo de datos.",
    publishedAt: "1 de septiembre de 2026",
    readingTime: "9 minutos",
    audience: "Para abogados y equipos jurídicos",
    icon: Bot,
    accent: "text-cyan-300",
    intro: [
      "La inteligencia artificial generativa puede ayudar a resumir documentos, ordenar argumentos, crear borradores y detectar preguntas pendientes. El riesgo aparece cuando la rapidez se confunde con certeza o cuando se incorporan antecedentes sensibles sin evaluar cómo serán tratados.",
      "La herramienta puede proponer. La responsabilidad profesional de revisar, decidir y explicar sigue siendo humana. Estos siete controles ofrecen un punto de partida para integrar IA sin convertirla en una caja negra.",
    ],
    sections: [
      {
        title: "1. Define la tarea antes de elegir la herramienta",
        paragraphs: [
          "No todas las tareas tienen el mismo riesgo. Generar una estructura de ideas no es equivalente a analizar una carpeta con nombres, domicilios, antecedentes familiares o información de salud. Describe el objetivo, identifica qué información sería necesaria y decide si la IA es realmente apropiada.",
        ],
      },
      {
        title: "2. Minimiza y anonimiza los datos",
        paragraphs: [
          "Evita ingresar información identificable cuando la tarea pueda resolverse con datos ficticios, iniciales o una versión anonimizada. Elimina detalles innecesarios y revisa si, aun sin el nombre, la combinación de antecedentes permitiría identificar a la persona.",
        ],
      },
      {
        title: "3. Revisa las condiciones de tratamiento de la plataforma",
        paragraphs: [
          "Antes de utilizar un servicio, verifica dónde se procesan los datos, cuánto tiempo se conservan, si pueden emplearse para entrenar modelos, qué controles de acceso existen y cómo se eliminan. Una cuenta personal gratuita puede ofrecer condiciones distintas a un servicio empresarial.",
        ],
      },
      {
        title: "4. Exige fuentes comprobables",
        paragraphs: [
          "Una respuesta fluida puede contener normas inexistentes, artículos equivocados o jurisprudencia inventada. Comprueba cada cita en la fuente oficial, confirma la versión vigente de la norma y verifica que el fallo corresponda realmente al tribunal, fecha y materia indicados.",
        ],
      },
      {
        title: "5. Mantén una revisión humana sustantiva",
        paragraphs: [
          "Revisar no es corregir ortografía. Implica contrastar hechos, detectar omisiones, examinar la lógica, evaluar riesgos y decidir si el resultado es adecuado para el caso concreto. Ningún escrito debería presentarse ni ninguna recomendación comunicarse sólo porque el texto parece convincente.",
        ],
      },
      {
        title: "6. Registra cómo se utilizó la IA",
        paragraphs: [
          "Para tareas relevantes, conserva una trazabilidad proporcional: finalidad, tipo de información utilizada, herramienta, fecha, verificaciones realizadas y persona que aprobó el resultado. El registro debe demostrar el proceso de control sin crear un nuevo riesgo.",
        ],
      },
      {
        title: "7. Prepárate para el nuevo estándar chileno de datos personales",
        paragraphs: [
          "Hasta el 30 de noviembre de 2026 continúa vigente el régimen actual de la Ley N.º 19.628. Las modificaciones introducidas por la Ley N.º 21.719 entran en vigor el 1 de diciembre de 2026 y refuerzan principios como finalidad, proporcionalidad, seguridad, transparencia y confidencialidad.",
          "Para un equipo jurídico, la transición es una oportunidad para inventariar herramientas, definir usos permitidos, limitar accesos y establecer un procedimiento frente a errores o incidentes.",
        ],
      },
    ],
    takeawayTitle: "La mejor política de IA comienza con límites claros",
    takeaway:
      "Una herramienta útil debe integrarse a un proceso verificable: datos mínimos, condiciones conocidas, fuentes comprobadas, revisión profesional y responsabilidad asignada. La velocidad sólo genera valor cuando el resultado conserva calidad y confianza.",
    sources: [
      {
        label: "Ley N.º 19.628: texto vigente hasta el 30 de noviembre de 2026",
        url: "https://www.bcn.cl/leychile/Navegar?idNorma=141599&idParte=8642686",
      },
      {
        label: "Ley N.º 21.719: nuevo régimen con vigencia desde el 1 de diciembre de 2026",
        url: "https://www.bcn.cl/leychile/Navegar?idNorma=1209272",
      },
    ],
    ctaEyebrow: "Tecnología jurídica en ColegaLink",
    ctaTitle: "Incorpora tecnología sin perder el criterio profesional.",
    ctaText:
      "Conoce los planes de ColegaLink y las herramientas diseñadas para apoyar la práctica jurídica.",
    ctaLabel: "Ver planes y beneficios",
    ctaPath: "/planes",
  },
};

function setSEO(article, slug) {
  const title = article.title + " · ColegaLink";
  document.title = title;
  const description = article.summary;
  const url = window.location.origin + "/blog/" + slug;

  const setMeta = (attribute, key, content) => {
    let element = document.querySelector(`meta[${attribute}="${key}"]`);
    if (!element) {
      element = document.createElement("meta");
      element.setAttribute(attribute, key);
      document.head.appendChild(element);
    }
    element.setAttribute("content", content);
  };

  setMeta("name", "description", description);
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:type", "article");
  setMeta("property", "og:url", url);
}

export default function EditorialArticle() {
  const { slug } = useParams();
  const article = ARTICLES[slug];

  useEffect(() => {
    if (article) setSEO(article, slug);
  }, [article, slug]);

  if (!article) {
    return (
      <main className="min-h-[70vh] bg-slate-950 px-6 py-24 text-center text-white">
        <h1 className="text-3xl font-black">Artículo no encontrado</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-teal-300">
          <ArrowLeft size={18} /> Volver al blog
        </Link>
      </main>
    );
  }

  const Icon = article.icon;

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <header className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(45,212,191,0.14),transparent_40%),radial-gradient(circle_at_left,rgba(34,211,238,0.10),transparent_34%)]" />
        <div className="container-app relative py-14 md:py-20">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-bold text-slate-300 transition hover:text-white">
            <ArrowLeft size={17} /> Blog ColegaLink
          </Link>
          <div className={"mt-8 inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] " + article.accent}>
            <Icon size={16} /> {article.category}
          </div>
          <h1 className="mt-5 max-w-5xl text-4xl font-black leading-tight md:text-6xl">{article.title}</h1>
          <p className="mt-6 max-w-4xl text-lg leading-8 text-slate-300">{article.summary}</p>
          <div className="mt-7 flex flex-wrap gap-3 text-sm text-slate-300">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2">
              <CalendarDays size={15} /> {article.publishedAt}
            </span>
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2">Lectura: {article.readingTime}</span>
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2">{article.audience}</span>
          </div>
        </div>
      </header>

      <div className="container-app grid gap-10 py-14 lg:grid-cols-[minmax(0,1fr)_320px]">
        <article className="max-w-4xl">
          {article.intro.map((paragraph) => (
            <p key={paragraph} className="mb-5 text-lg leading-8 text-slate-200">{paragraph}</p>
          ))}

          {article.sections.map((section) => (
            <section key={section.title} className="mt-12 border-t border-white/10 pt-9">
              <h2 className="text-2xl font-black md:text-3xl">{section.title}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph} className="mt-5 leading-8 text-slate-300">{paragraph}</p>
              ))}
              {section.list && (
                <ul className="mt-6 space-y-4">
                  {section.list.map((item) => (
                    <li key={item} className="flex items-start gap-3 leading-7 text-slate-300">
                      <CheckCircle2 className="mt-1 shrink-0 text-teal-300" size={18} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <section className="mt-12 rounded-3xl border border-teal-300/25 bg-teal-300/[0.07] p-7 md:p-9">
            <ShieldCheck className="text-teal-300" size={28} />
            <h2 className="mt-4 text-2xl font-black">{article.takeawayTitle}</h2>
            <p className="mt-4 leading-8 text-slate-200">{article.takeaway}</p>
          </section>

          <p className="mt-8 text-sm leading-6 text-slate-400">
            Este contenido es informativo y no reemplaza la asesoría jurídica para un caso concreto.
          </p>
        </article>

        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
            <h2 className="text-lg font-black">Fuentes oficiales</h2>
            <div className="mt-4 space-y-4">
              {article.sources.map((source) => (
                <a
                  key={source.url}
                  href={source.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-start gap-2 text-sm leading-6 text-teal-300 transition hover:text-teal-200"
                >
                  <ExternalLink className="mt-1 shrink-0" size={15} /> {source.label}
                </a>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-cyan-300/20 bg-gradient-to-br from-cyan-950 to-slate-950 p-6">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-cyan-300">{article.ctaEyebrow}</p>
            <h2 className="mt-4 text-2xl font-black">{article.ctaTitle}</h2>
            <p className="mt-4 text-sm leading-6 text-slate-300">{article.ctaText}</p>
            <Link to={article.ctaPath} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-teal-300 px-4 py-3 text-sm font-black text-slate-950 transition hover:bg-teal-200">
              {article.ctaLabel} <ArrowRight size={16} />
            </Link>
          </section>
        </aside>
      </div>
    </main>
  );
}
